/* ReturnToPupilCard
 * NDG-JOURNEY-01 · Feb 2026
 * Canonical Return-to-Pupil surface. Reads from the SINGLE
 * canonical authority /api/work-mode/return-deadline which is
 * backed by JourneyTeachingGeographyService.return_deadline.
 * Never computes routes or straight-line geometry on the client.
 *
 * Location source (§17 founder direction):
 *   1. `currentLocation` prop (LIVE_LOCATION when caller has one)
 *   2. navigator.geolocation.getCurrentPosition (LIVE_LOCATION)
 *   3. `previousBookingLocation` prop (PREVIOUS_BOOKING_LOCATION)
 *   4. no location → refuse cleanly
 *
 * States rendered honestly (no invented return times):
 *   OK             → "Leave by HH:MM" plus km / min drive
 *   NOT_CONFIGURED → routing not configured
 *   PUPIL_LOCATION_UNRESOLVED
 *   ROUTING_UNAVAILABLE / anything else → calm refusal
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SURFACE = {
  dark: {
    root:   "rounded-2xl border border-slate-700 bg-slate-800/70 p-4",
    label:  "text-[10px] uppercase tracking-wide font-bold text-slate-400",
    hero:   "text-2xl font-extrabold text-white",
    sub:    "text-xs text-slate-300 mt-1",
    quiet:  "text-sm text-slate-400",
    notice: "text-sm text-amber-200",
  },
  light: {
    root:   "rounded-2xl border border-slate-200 bg-white p-4",
    label:  "text-[10px] uppercase tracking-wide font-bold text-slate-400",
    hero:   "text-2xl font-extrabold text-slate-900",
    sub:    "text-xs text-slate-500 mt-1",
    quiet:  "text-sm text-slate-500",
    notice: "text-sm text-amber-700",
  },
};

export default function ReturnToPupilCard({
  lessonId,
  date,
  lessonEndTime,
  pupilId,
  currentLocation,               // {lat, lng} — LIVE if caller has it
  currentLocationSource,         // override the origin source string
  previousBookingLocation,       // {lat, lng} — fallback
  surface = "light",
}) {
  const T = SURFACE[surface] || SURFACE.light;
  const [origin, setOrigin] = useState(null);          // {loc, source}
  const [state, setState] = useState({ loading: false, data: null, error: null });

  // 1. Resolve origin location (§17). Prop → geolocation → previous_booking.
  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (currentLocation && Number.isFinite(currentLocation.lat)) {
        if (!cancelled) setOrigin({
          loc: currentLocation,
          source: currentLocationSource || "LIVE_LOCATION",
        });
        return;
      }
      if (typeof navigator !== "undefined" && navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              maximumAge: 60_000, timeout: 8_000, enableHighAccuracy: false,
            })
          );
          if (!cancelled && pos?.coords) {
            setOrigin({
              loc: { lat: pos.coords.latitude, lng: pos.coords.longitude },
              source: "LIVE_LOCATION",
            });
            return;
          }
        } catch (_e) {
          /* fall through to previousBookingLocation */
        }
      }
      if (previousBookingLocation && Number.isFinite(previousBookingLocation.lat)) {
        if (!cancelled) setOrigin({
          loc: previousBookingLocation,
          source: "PREVIOUS_BOOKING_LOCATION",
        });
        return;
      }
      if (!cancelled) setOrigin({ loc: null, source: "MANUAL_LOCATION" });
    }
    resolve();
    return () => { cancelled = true; };
  }, [currentLocation, currentLocationSource, previousBookingLocation]);

  // 2. Fetch canonical deadline once origin known.
  const fetchDeadline = useCallback(async () => {
    if (!origin) return;
    if (!origin.loc) {
      setState({
        loading: false, data: null,
        error: null,
      });
      return;
    }
    if (!lessonId && (!date || !lessonEndTime)) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await axios.post(`${API}/work-mode/return-deadline`, {
        lesson_id: lessonId || null,
        date: date || null,
        lesson_end_time: lessonEndTime || null,
        current_location: origin.loc,
        current_location_source: origin.source,
        pupil_id: pupilId || null,
      }, { withCredentials: true });
      setState({ loading: false, data: res.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data || String(e) });
    }
  }, [lessonId, date, lessonEndTime, pupilId, origin]);

  useEffect(() => { fetchDeadline(); }, [fetchDeadline]);

  const d = state.data;
  const status = d?.status;
  const leaveBy = d?.latest_safe_departure_iso
    ? d.latest_safe_departure_iso.split("T")[1]?.slice(0, 5)
    : null;

  const noLocation = origin && origin.loc == null;

  return (
    <div data-testid="return-to-pupil-card" className={T.root}>
      <div className="flex items-center justify-between">
        <p className={T.label}>Return to pupil</p>
        {origin?.source && !noLocation && (
          <p
            className="text-[9px] uppercase tracking-wide font-semibold text-slate-500"
            data-testid="return-origin-source"
          >
            {origin.source.replace(/_/g, " ").toLowerCase()}
          </p>
        )}
      </div>

      {state.loading && (
        <p className={`mt-2 ${T.quiet}`} data-testid="return-loading">
          Checking real road-traffic time…
        </p>
      )}

      {noLocation && (
        <p className={`mt-2 ${T.notice}`} data-testid="return-status-no-location">
          Waiting for a location before we can plan the drive back.
        </p>
      )}

      {status === "OK" && leaveBy && (
        <div className="mt-1">
          <p className={T.hero} data-testid="return-leave-by">
            Leave by {leaveBy}
          </p>
          <p className={T.sub}>
            To arrive at pupil for lesson end · traffic-aware
            {d?.distance_meters ? ` · ${Math.round(d.distance_meters / 100) / 10} km` : ""}
            {d?.traffic_duration_seconds ? ` · ${Math.round(d.traffic_duration_seconds / 60)} min drive` : ""}
          </p>
        </div>
      )}

      {status && status !== "OK" && (
        <p
          className={`mt-1 ${T.notice}`}
          data-testid={`return-status-${String(status).toLowerCase().replace(/_/g, "-")}`}
        >
          {status === "ROUTING_PROVIDER_NOT_CONFIGURED"
            ? "Return time unavailable — routing not configured on the server."
            : status === "PUPIL_LOCATION_UNRESOLVED"
              ? "Return time unavailable — pupil pickup address not resolved yet."
              : `Return time unavailable — ${status.toLowerCase().replace(/_/g, " ")}.`}
        </p>
      )}

      {state.error && (
        <p className={`mt-1 ${T.notice}`} data-testid="return-error">
          {typeof state.error === "string" ? state.error : JSON.stringify(state.error)}
        </p>
      )}
    </div>
  );
}
